import {MAINTENANCE_EVENT, normalizeMaintenanceState} from "@/lib/platform";
import {withRequestDeadline} from "@/lib/request-deadline";

export interface ApiClientErrorPayload {
    isOk?: boolean;
    status?: string;
    message?: string;
    detail?: unknown;
    [key: string]: unknown;
}

export class ApiClientError extends Error {
    statusCode: number;
    payload: ApiClientErrorPayload | null;

    constructor(statusCode: number, payload: ApiClientErrorPayload | null) {
        const rawMessage = payload?.message;
        const message = typeof rawMessage === "string"
            ? rawMessage
            : rawMessage == null
                ? `API request failed (status ${statusCode})`
                : JSON.stringify(rawMessage);
        super(message);
        this.statusCode = statusCode;
        this.payload = payload;
    }
}

export interface ApiRequestOptions {
    path: string;
    method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
    body?: unknown;
    formData?: FormData;
    headers?: HeadersInit;
    signal?: AbortSignal;
    responseType?: "json" | "blob";
    timeoutMs?: number;
}

function apiBase() {
    const base = (process.env.NEXT_PUBLIC_API_URI ?? "").trim() || "http://localhost:8000/api/v1";
    return base.endsWith("/") ? base.slice(0, -1) : base;
}

function normalizeValidationMessage(detail: unknown): string | undefined {
    if (!Array.isArray(detail) || detail.length === 0) {
        return undefined;
    }
    const first = detail[0];
    if (!first || typeof first !== "object") {
        return undefined;
    }
    const msg = (first as Record<string, unknown>).msg;
    return typeof msg === "string" ? msg : undefined;
}

function isBodyInitValue(value: unknown): value is BodyInit {
    return typeof value === "string"
        || value instanceof Blob
        || value instanceof URLSearchParams
        || value instanceof ArrayBuffer
        || ArrayBuffer.isView(value)
        || value instanceof ReadableStream
        || value instanceof FormData;
}

export async function apiRequest<T>(options: ApiRequestOptions): Promise<T> {
    const { path, method = "GET", body, formData, headers, signal, responseType = "json", timeoutMs = 15000 } = options;
    if (body !== undefined && formData !== undefined) {
        throw new Error("Only one of body or formData is allowed");
    }

    const finalHeaders = new Headers(headers ?? {});
    let requestBody: BodyInit | undefined;

    if (formData !== undefined) {
        requestBody = formData;
    } else if (body !== undefined) {
        if (isBodyInitValue(body)) {
            requestBody = body;
        } else {
            finalHeaders.set("Content-Type", "application/json");
            requestBody = JSON.stringify(body);
        }
    }

    try {
        return await withRequestDeadline(async requestSignal => {
        const response = await fetch(`${apiBase()}${path}`, {
            method,
            credentials: "include",
            headers: finalHeaders,
            body: requestBody,
            signal: requestSignal,
            cache: "no-store",
        });

    if (response.ok && responseType === "blob") return await response.blob() as T;
    const rawText = await response.text();
    let data: unknown = null;
    if (rawText) {
        try {
            data = JSON.parse(rawText);
        } catch {
            data = rawText;
        }
    }

    const envelope = (data && typeof data === "object") ? data as Record<string, unknown> : null;
    const detail = envelope?.detail;

    const maintenancePayload = detail && typeof detail === "object" && !Array.isArray(detail)
        ? detail as Record<string, unknown>
        : envelope;
    const maintenanceActive = maintenancePayload?.status === "MAINTENANCE"
        || maintenancePayload?.maintenance === true;

    if (maintenanceActive && typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(MAINTENANCE_EVENT, {
            detail: normalizeMaintenanceState({
                enabled: true,
                message: maintenancePayload?.message,
                updatedAt: null,
            }),
        }));
    }

    if (!response.ok) {
        let payload = (detail && typeof detail === "object" && !Array.isArray(detail))
            ? detail as ApiClientErrorPayload
            : (envelope as ApiClientErrorPayload | null);
        const validationMessage = normalizeValidationMessage(detail);
        if (payload && !payload.message && validationMessage) {
            payload.message = validationMessage;
        }
        if (!payload) {
            payload = rawText ? { message: rawText } : { message: response.statusText || `HTTP ${response.status}` };
        }
        throw new ApiClientError(response.status, payload);
    }

    return data as T;
        }, {signal, timeoutMs});
    } catch (e) {
        if (e instanceof ApiClientError || (e instanceof Error && e.name === "AbortError")) throw e;
        const msg = e instanceof Error ? e.message : String(e);
        throw new ApiClientError(0, {message: msg});
    }
}
